import { type UserBusinessPartner } from '@sr/types';
import {
  co2impactArticleDataByCountry,
  csrdPremiumArticleDataByCountry,
  isFromDenmark,
  isFromNorway,
  isFromSweden,
  getCountryFromDwKey,
} from '@sr/utils';

/**
 * Filters business partners that are Swedish or Danish with Follow-up service
 * and have the CO2 Impact service enabled
 */
export const getPartnersWithCo2Impact = (
  businessPartners: UserBusinessPartner[] | undefined,
): string[] => {
  if (!businessPartners) return [];

  const followUpPartners = businessPartners.filter((bp) => {
    const country = getCountryFromDwKey(bp.id);
    const articleData = co2impactArticleDataByCountry(country);
    const isFollowup = bp.services.some((service) => service.name === 'services.follow.up.name');

    return articleData.active && isFollowup;
  });

  return followUpPartners
    .filter((bp) => bp.services.some((s) => s.co2Impact))
    .flatMap((bp) => [bp.id, ...(bp.expandedPartners ?? [])]);
};

/**
 * Filters business partners that are Swedish or Danish with Follow-up service
 * and have the CSRD Premium service enabled
 */
export const getPartnersWithCsrdPremium = (
  businessPartners: UserBusinessPartner[] | undefined,
): string[] => {
  if (!businessPartners) return [];

  const followUpPartners = businessPartners.filter((bp) => {
    const country = getCountryFromDwKey(bp.id);
    const articleData = csrdPremiumArticleDataByCountry(country);
    const isFollowup = bp.services.some((service) => service.name === 'services.follow.up.name');

    return articleData.active && isFollowup;
  });

  return followUpPartners
    .filter((bp) => bp.services.some((s) => s.csrdPremium || s.csrdEnterprise))
    .flatMap((bp) => [bp.id, ...(bp.expandedPartners ?? [])]);
};

/**
 * Filters business partners that are Swedish, Danish or Norwegian with Follow-up service
 * and have the CSRD Enterprise service enabled
 */
export const getPartnersWithCsrdEnterprise = (
  businessPartners: UserBusinessPartner[] | undefined,
): string[] => {
  if (!businessPartners) return [];

  const followUpPartners = businessPartners.filter((bp) => {
    const country = getCountryFromDwKey(bp.id);
    const articleData = csrdPremiumArticleDataByCountry(country);
    const isFollowup = bp.services.some((service) => service.name === 'services.follow.up.name');

    return articleData.active && isFollowup;
  });

  const partnersWithEnterprise = followUpPartners.filter((bp) =>
    bp.services.some((s) => s.csrdEnterprise),
  );

  return partnersWithEnterprise.flatMap((bp) => [bp.id, ...(bp.expandedPartners ?? [])]);
};

/**
 * Gets all root partners that are Swedish or Danish with Follow-up service
 */
export const getFollowUpRootPartners = (
  businessPartners: UserBusinessPartner[] | undefined,
): UserBusinessPartner[] => {
  if (!businessPartners) return [];

  return businessPartners.filter((bp) => {
    const isFollowUpCountry = isFromSweden(bp.id) || isFromDenmark(bp.id) || isFromNorway(bp.id);
    const isFollowup = bp.services.some((service) => service.name === 'services.follow.up.name');

    return isFollowUpCountry && isFollowup;
  });
};
